/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { FC, useEffect, useState, useMemo, memo, lazy, Suspense } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { size, Types } from '@marriott/mi-ui-library';
import { useMounted } from '../../hooks/useMounted';
import { shouldUseMockData } from '../../utils/CommonUtils';
import { InfoBlockSkeletonLoader } from './InfoBlockSkeletonLoader';

const VariableColumnContainer = lazy(() =>
  import('@marriott/mi-common-static-components').then(mod => ({ default: mod.VariableColumnContainer }))
);

export const InfoBlockWrapper: FC<any> = memo((props: any) => {
  const { model, mbeData, isAuthorMode } = props;
  const [mockdataInAuthorMode, setMockDataInAuthorMode] = useState<any>(null);
  const isMounted = useMounted();

  const infoBlockData = useMemo(() => {
    const data = isAuthorMode ? mockdataInAuthorMode : mbeData;
    return data?.responseObject?.offers?.mbeOfferById?.offers?.moreInfo;
  }, [isAuthorMode, mockdataInAuthorMode, mbeData]);

  const infoBlock = infoBlockData?.infoBlock;
  const infoBlockLength = infoBlock?.length ?? 0;

  // Load mock model dynamically only for dev mode
  useEffect(() => {
    let isCancelled = false;

    const fetchMockData = async () => {
      if (shouldUseMockData(isAuthorMode)) {
        const mockModule = await import('./__mock__/InfoBlockAPI.mock');
        if (!isCancelled) {
          setMockDataInAuthorMode(mockModule.InfoBlockAPIdataMock);
        }
      }
    };

    fetchMockData();

    return () => {
      isCancelled = true;
    };
  }, [isAuthorMode]);

  const updatedModel = useMemo(() => {
    const modelCQitems = {
      ':items': {} as any,
      ':itemsOrder': [] as string[],
    };

    infoBlock?.forEach((infoItemData: any, index: number) => {
      const infoItem = `variable-column-${index}`;
      modelCQitems[':itemsOrder'].push(infoItem);

      modelCQitems[':items'][infoItem] = {
        ':items': {
          datablock: {
            headerTag: 'h2',
            assetVariation: 'iconFont',
            impressionTrack: 'false',
            description: `<p>${infoItemData?.description}</p>\r\n`,
            trackingProperties: {
              enableScrollingBehavior: false,
              trackingDescription: '',
              impressionTrack: false,
              description: `<p>${infoItemData?.description}</p>\r\n`,
              trackingContentPosition: '',
              atCCeVar48: '',
              rightArrowDesc: '',
              cardLocation: '',
              leftArrowDesc: '',
              clickTrack: false,
              location: '',
              merchandisingCategory: 'nonCobrand',
              impressionCount: false,
              trackingTag: '',
              trackingOfferType: '',
            },
            isAuthor: false,
            iconAltText: `${infoItemData?.icon}`,
            ':type': 'mi-aem-common-spa/components/content/datablock',
            headerFontSize: 't-subtitle-m',
            dataBlockVariations: 'icon',
            iconSize: 'large',
            merchandisingCategory: 'nonCobrand',
            impressionCount: 'false',
            iconFontPath: `${infoItemData?.icon}`,
            headingType: 'subtitle',
            orientation: 'horizontal',
            styleclass: 'coral',
            displayRule: 'horizontal',
            header: `${infoItemData?.title}`,
            wcmMode: 'disabled',
          },
        },
        allowedComponents: {
          components: [],
          applicable: false,
        },
        ':type': 'mi-aem-common-spa/components/container',
        ':itemsOrder': ['datablock'],
        columnClassNames: {
          datablock: 'aem-GridColumn aem-GridColumn--default--12',
        },
        gridClassNames: 'aem-Grid aem-Grid--12 aem-Grid--default--12',
        columnCount: 12,
      };
    });

    return {
      contentAlignment: 'left',
      desktopColumns: infoBlockLength > 3 ? '4' : '3',
      tabletColumns: infoBlockLength > 3 ? '2' : '3',
      headerTag: 'h2' as Types.tags,
      headerFontSize: 't-title-m' as size.medium,
      header: infoBlockLength > 0 ? `<p>${model?.title || infoBlockData?.title}</p>\r\n` : '',
      descFontSize: size.medium,
      descriptionText: `${infoBlockData?.disclaimer || ''}`,
      reverseStacking: false,
      mobileColumns: '1',
      modelFactory: {},
      ...modelCQitems,
      ...props?.model,
    };
  }, [infoBlock, infoBlockLength, model?.title, infoBlockData?.title, infoBlockData?.disclaimer, props?.model]);

  const componentProps = useMemo(
    () => ({
      model: updatedModel,
      allowedComponents: props?.allowedComponents,
    }),
    [updatedModel, props?.allowedComponents]
  );

  if (infoBlockLength === 0) {
    return null;
  }

  if (!isMounted) {
    return <InfoBlockSkeletonLoader CARD_COUNT={3} />;
  }

  return (
    <Suspense fallback={<InfoBlockSkeletonLoader CARD_COUNT={3} />}>
      <VariableColumnContainer {...componentProps} />
    </Suspense>
  );
});

export const InfoBlockConfig = {
  emptyLabel: 'InfoBlock',
  isEmpty: () => true,
  resourceType: 'mi-aem-offers-spa/components/infoblock',
};

export const InfoBlock = (props: any) => (
  <div data-testid="offers-InfoBlock" data-component-name="o-offers-InfoBlock">
    <EditableComponent config={InfoBlockConfig} {...props}>
      <InfoBlockWrapper {...props} />
    </EditableComponent>
  </div>
);
